import { format, isAfter, isBefore, isValid, parse } from 'date-fns'
import { TFunction } from 'i18next'

import { API_DATE_FORMAT, SupportedDateFormatType } from '@utils/date.utils'

import { DateRangeFormatConfig } from './DateRangeFromTo'

const DATE_ENTER_INCOMPLETE = 'incomplete' as const
export type DateRangeParserReturnType = Date | null | typeof DATE_ENTER_INCOMPLETE

const makeParser =
  (formatStr: string) =>
  (value: string): DateRangeParserReturnType => {
    if (value.includes('d') || value.includes('m') || value.includes('y')) {
      return DATE_ENTER_INCOMPLETE
    }

    const digits = value.replace(/\D/g, '')
    if (digits.length < 8) {
      return DATE_ENTER_INCOMPLETE
    }

    const parsed = parse(value, formatStr, new Date())

    if (!isValid(parsed)) {
      return null
    }

    return format(parsed, formatStr) === value ? parsed : null
  }

const makeFormatter =
  (formatStr: string) =>
  (date: Date): string =>
    format(date, formatStr)

export const DATE_FORMAT_CONFIG: Partial<Record<SupportedDateFormatType, DateRangeFormatConfig>> = {
  'yyyy-MM-dd': {
    placeholder: 'yyyy-mm-dd',
    format: makeFormatter('yyyy-MM-dd'),
    parse: makeParser('yyyy-MM-dd'),
    imaskPattern: 'Y-`m-`d',
  },
  'dd/MM/yyyy': {
    placeholder: 'dd/mm/yyyy',
    format: makeFormatter('dd/MM/yyyy'),
    parse: makeParser('dd/MM/yyyy'),
    imaskPattern: 'd/`m/`Y',
  },
  'MM/dd/yyyy': {
    placeholder: 'mm/dd/yyyy',
    format: makeFormatter('MM/dd/yyyy'),
    parse: makeParser('MM/dd/yyyy'),
    imaskPattern: 'm/`d/`Y',
  },
}

export const DEFAULT_DATE_FORMAT: SupportedDateFormatType = API_DATE_FORMAT

export const getDateRangeFormatConfig = (dateFormat?: string | null): DateRangeFormatConfig =>
  DATE_FORMAT_CONFIG[dateFormat as SupportedDateFormatType] ??
  DATE_FORMAT_CONFIG[DEFAULT_DATE_FORMAT]!

const normalizeStartOfDay = (date: Date): Date => {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d
}

const normalizeEndOfDay = (date: Date): Date => {
  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d
}

// Date-based validation (no parsing / config involved)

export const getDateRangeStartDateError = (data: {
  date: Date
  minDate?: Date
  maxDate?: Date
  t: TFunction
}): string | undefined => {
  const { date, minDate, maxDate = new Date(), t } = data

  if (maxDate && isAfter(date, normalizeEndOfDay(maxDate))) {
    return t('Validation.Future dates not allowed')
  }

  if (minDate && isBefore(date, normalizeStartOfDay(minDate))) {
    const years = new Date().getFullYear() - minDate.getFullYear()
    return t('Validation.Max {{years}} years ago', { years })
  }

  return undefined
}

export const getDateRangeEndDateError = (data: {
  date: Date
  startDate?: Date
  minDate?: Date
  maxDate?: Date
  t: TFunction
}): string | undefined => {
  if (data.startDate && isAfter(data.startDate, data.date)) {
    return data.t('Validation.End date must be after start date')
  }
  return getDateRangeStartDateError(data)
}

// String-based validation (parse first, then delegate)

const parseDateRangeInput = (data: {
  value: string
  config: DateRangeFormatConfig
  t: TFunction
}): { error?: string; date?: Date } => {
  const { value, config, t } = data

  if (value.length === 0) {
    return {}
  }

  const parsed = config.parse(value)
  if (parsed === DATE_ENTER_INCOMPLETE) {
    return {}
  }

  if (parsed === null) {
    return { error: t('Validation.Please enter a valid date') }
  }

  return { date: parsed }
}

export const getDateRangeInputStartError = (data: {
  value: string
  config: DateRangeFormatConfig
  minDate?: Date
  maxDate?: Date
  t: TFunction
}): string | undefined => {
  const { error, date } = parseDateRangeInput(data)
  if (error || !date) {
    return error
  }
  return getDateRangeStartDateError({ ...data, date })
}

export const getDateRangeInputEndError = (data: {
  value: string
  config: DateRangeFormatConfig
  startDate?: Date
  minDate?: Date
  maxDate?: Date
  t: TFunction
}): string | undefined => {
  const { error, date } = parseDateRangeInput(data)
  if (error || !date) {
    return error
  }
  return getDateRangeEndDateError({ ...data, date })
}
